import React from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useSlideBackground} from '../../../../hooks/useSlideBackground';
import {DeviceType} from '../../../../incase-domain/common/DeviceType.enum';
import Button from '../../../../incase-domain/components/button/Button';
import ChecklistTile from '../../../../incase-domain/components/checkbox/ChecklistTile';
import ColorSelectCompact from '../../../../incase-domain/components/select/compact/color/colorSelectCompact/ColorSelectCompact';
import ImageSelectCompact from '../../../../incase-domain/components/select/compact/image/ImageSelectCompact';
import {ISlide} from '../../../../incase-domain/slide';
import {setSidePanelContentAction, switchGridAction} from '../../../../store/actions/grid';
import {SidePanelContent} from '../../../../store/actions/types/grid.types';
import {showGridSelector} from '../../../../store/selectors/grid';
import {selectedViewSelector} from '../../../../store/selectors/presentation.selectors';
import SectionTitle from '../../../elements/common/SectionTitle';
import DeviceSelection from './DeviceSelection';
import ElementList from './ElementList';
import SlideSettingsHeader from './SlideSettingsHeader';
import SlideSettingsMobileLayout from './SlideSettingsMobileLayout';
import {uploadImageFile} from '../../../../service/files.service';

interface SlideSettingsFormProps {
    slide: ISlide;
}

function SlideSettingsForm({slide}: SlideSettingsFormProps): JSX.Element {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const showGrid = useSelector(showGridSelector);
    const selectedView = useSelector(selectedViewSelector);

    const {slideBackgroundColor, slideBackgroundImage, slideMobileBackgroundImage, setSlideBackgroundColor, setSlideBackgroundImage} =
        useSlideBackground();

    return (
        <div className="c-slideSettings">
            <div className="c-slideSettings__body">
                <div className="c-slideSettings__titleInput">
                    <SlideSettingsHeader key={slide.name} slide={slide} />
                </div>

                <div className="l-group--form">
                    <SectionTitle
                        label={t('presEditPage.slideSettingsForm.devices')}
                        infoText={t('presEditPage.slideSettingsForm.devicesInfo')}
                    />
                    <DeviceSelection />
                </div>

                <div className="l-group--form">
                    <SectionTitle
                        label={
                            selectedView === DeviceType.DESKTOP
                                ? t('presEditPage.slideSettingsForm.background')
                                : t('presEditPage.slideSettingsForm.mobileBackground')
                        }
                    />
                    <div className="u-flex u-flex-col u-gap-2">
                        <ColorSelectCompact
                            label={t('presEditPage.slideSettingsForm.slideColor')}
                            onChange={(e) => setSlideBackgroundColor(e)}
                            color={slideBackgroundColor}
                        />
                        <ImageSelectCompact
                            label={t('presEditPage.slideSettingsForm.bgImg')}
                            onChange={setSlideBackgroundImage}
                            image={{
                                image: (selectedView === DeviceType.DESKTOP ? slideBackgroundImage : slideMobileBackgroundImage) || '',
                                name: '',
                            }}
                            onUpload={uploadImageFile}
                        />
                    </div>
                </div>

                <div className="l-group--form">
                    <SectionTitle label={t('presEditPage.slideSettingsForm.preferences')} />
                    <ChecklistTile
                        value={showGrid}
                        label={t('presEditPage.themeModal.grid')}
                        onChange={() => dispatch(switchGridAction())}
                    />
                </div>

                {selectedView === DeviceType.MOBILE && (
                    <div className="l-group--form">
                        <SectionTitle label={t('presEditPage.slideSettingsForm.mobileLayout')} />
                        <SlideSettingsMobileLayout slide={slide} />
                    </div>
                )}

                {slide.elements.length > 0 && (
                    <>
                        <div className="l-group--form">
                            <SectionTitle label={t('presEditPage.slideSettingsForm.elements')} />
                        </div>

                        <ElementList elements={slide.elements} />
                    </>
                )}
            </div>

            <div className="c-slideSettings__footer">
                <Button
                    className="c-slideSettings__footer_button"
                    color="secondary"
                    fullWidth
                    variant="ghost"
                    size="lg"
                    onClick={() => dispatch(setSidePanelContentAction(SidePanelContent.ELEMENTS))}
                    leading="ph:plus"
                    dataCy="element-sidepanel-open-button"
                >
                    {t('designSidepanel.addElement')}
                </Button>
            </div>
        </div>
    );
}

export default SlideSettingsForm;
